import { useState } from 'react';

import { useBrand } from './useBrand';

import darkMark from '@/assets/flyst-mark-dark.png';
import mark from '@/assets/flyst-mark.png';
import darkWordmark from '@/assets/flyst-wordmark-dark.png';
import wordmark from '@/assets/flyst-wordmark.png';

import styles from './BrandLockup.module.css';

export type BrandLockupProps = {
  /**
   * `wordmark` is the full logo, name and all. `mark` is the globe on its own, for
   * slots too narrow to take it. The collapsed sidebar rail is the only one.
   */
  variant?: 'wordmark' | 'mark';
  /** Which surface this sits on. The logo is light artwork; on white it needs ink. */
  tone?: 'light' | 'dark';
  /** Rendered height in pixels. Width follows the artwork's own proportions. */
  height?: number;
  className?: string | undefined;
};

/**
 * The Flyst logo.
 *
 * The artwork carries the name and "OVERSEAS" inside it, so nothing is written beside
 * it: a lockup that sets the name again next to a logo that already says it reads as
 * a mistake, and screen readers would hear it twice. That is why this replaced the
 * old glyph-plus-two-lines-of-text arrangement rather than being dropped into it: the
 * text was not decoration to be restyled, it was a duplicate.
 *
 * Two tones because the logo is light artwork on a transparent background. On the
 * navy panel it is used as drawn; on a white surface the same artwork inked in the
 * brand navy, its shading preserved rather than flattened to a silhouette.
 *
 * An uploaded company logo takes precedence. An arbitrary organisation's logo cannot
 * be assumed to spell out its own name the way this one does, so if one is ever set,
 * whatever renders it should carry the name alongside.
 */
export function BrandLockup({
  variant = 'wordmark',
  tone = 'dark',
  height = 32,
  className,
}: BrandLockupProps) {
  const { logo, name } = useBrand();
  const [failedUrl, setFailedUrl] = useState<string | null>(null);

  const uploaded = logo && failedUrl !== logo ? logo : null;

  const artwork =
    variant === 'mark'
      ? { light: darkMark, dark: mark }
      : { light: darkWordmark, dark: wordmark };

  const source = uploaded ?? artwork[tone];

  return (
    <img
      src={source}
      // Named, unlike the old mark: there is no longer any text beside it to carry the
      // organisation's name, so if this is not announced the sign-in screen opens with
      // nothing identifying it at all.
      alt={name}
      style={{ height, width: 'auto' }}
      className={className ? `${styles.logo} ${className}` : styles.logo}
      onError={() => {
        if (uploaded) setFailedUrl(uploaded);
      }}
    />
  );
}
